import { Component, effect, inject, Signal, untracked } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { InsuredObject } from '@models/insured-object';
import { OrderStepBaseComponent } from 'app/order/order-step-base-component';
import { OrderService } from 'app/order/state/order.service';
import { OrderState } from 'app/order/models/order-state.model';
import { CommonValidators } from 'app/shared/validators/common-validators';
import { getOrderStateTag } from 'app/order/fns';
import { toSignal } from '@angular/core/rxjs-interop';
import { debounceTime, filter, map, startWith } from 'rxjs';

@Component({
    template: '',
    standalone: false,
})
export default class ElectronicsLandingBase extends OrderStepBaseComponent<Pick<OrderState, 'insuredObjects'>> {
    private orderService = inject(OrderService);

    protected hasDeductible = getOrderStateTag('hasDeductible') as Signal<boolean>;
    protected hasTheftCoverage = getOrderStateTag('hasTheftCoverage') as Signal<boolean>;
    protected hasMonthlySettlementPeriod = getOrderStateTag('hasMonthlySettlementPeriod') as Signal<boolean>;
    protected boughtInLastTwoWeeks = getOrderStateTag('boughtInLastTwoWeeks') as Signal<boolean>;

    public get insuredObjects(): InsuredObject[] {
        return this.state?.insuredObjects || [];
    }

    public toggleDeductible(deductible: boolean): void {
        this.orderService.setTag('hasDeductible', deductible);
    }

    public toggleTheftCoverage(coverage: boolean): void {
        this.orderService.setTag('hasTheftCoverage', coverage);
    }

    public toggleSettlementPeriod(monthly: boolean): void {
        this.orderService.setTag('hasMonthlySettlementPeriod', monthly);
    }

    public toggleBoughtInLastTwoWeeks(boughtInLastTwoWeeks: boolean): void {
        this.orderService.setTag('boughtInLastTwoWeeks', boughtInLastTwoWeeks);
    }

    public handleStateChange(): void {
        if (this.insuredObjects.length > 0) {
            this.form.patchValue({ ...this.insuredObjects[0] });
        }
    }

    public getData(): Pick<OrderState, 'insuredObjects'> {
        const objects = [...this.insuredObjects];
        objects.splice(0, 1);
        return {
            insuredObjects: [{ ...this.form.value, value: this.form.get('value').value }, ...objects],
        };
    }

    /**
     * @description sets up the form and effect for price updates
     * based on state tags and changes to the form.
     * **Please note:** This method may only be called in an injection
     * context (i.e. from the constructor of the class that extends this
     * base class)
     */
    protected initForm(deviceType: 'Smartphone' | null = null): void {
        this.form = inject(FormBuilder).group({
            type: [deviceType, Validators.required],
            description: [''],
            detail: [''],
            date: [null],
            model: [''],
            value: ['', CommonValidators.priceBetween(1, 2500).concat(Validators.required)],
        });

        const insuredObjectsFormValue = toSignal(
            this.form.valueChanges.pipe(
                startWith(this.form.value),
                debounceTime(300),
                filter(() => this.form.valid),
                map((insuredObject: InsuredObject) => ({ ...insuredObject, value: insuredObject.value })),
            ),
        );

        // Update the price when the form or one of the tags changes
        effect(() => {
            const insuredObjects = insuredObjectsFormValue();
            if (!insuredObjects) {
                return;
            }

            this.hasDeductible();
            this.hasTheftCoverage();
            this.hasMonthlySettlementPeriod();
            this.boughtInLastTwoWeeks();

            untracked(() => this.priceService.updatePrice({ insuredObjects: [insuredObjects] }));
        });
    }
}
